// 2026-09-03: Recharge QR safety boundary. USDT QR payloads must bind the
// official TRC20 contract, exact order amount and destination before display.
export const TRON_USDT_CONTRACT = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t'

export type RechargeUsdtQrMode = 'amount' | 'address'

export interface RechargePaymentQrOrder {
  assetCode?: string | null
  orderId?: string | number | null
  orderNo?: string | number | null
  expectedAmount?: string | number | null
  receiveAddress?: string | null
  paymentQrContent?: string | null
  paymentQrType?: string | null
}

type TokenPocketTransferPayload = {
  protocol?: unknown
  version?: unknown
  action?: unknown
  actionId?: unknown
  blockchain?: unknown
  to?: unknown
  amount?: unknown
  contract?: unknown
  symbol?: unknown
  precision?: unknown
}

const normalized = (value: unknown) => String(value ?? '').trim()
const normalizedUpper = (value: unknown) => normalized(value).toUpperCase()

export const isUsdtRechargeAsset = (assetCode: unknown) => [
  'USDT',
  'USDT_TRC20',
  'USDT-TRC20',
].includes(normalizedUpper(assetCode))

const parseTokenPocketPayload = (order: RechargePaymentQrOrder | null | undefined) => {
  if (!order || !isUsdtRechargeAsset(order.assetCode)) return null
  if (![
    'TOKENPOCKET_USDT_TRC20',
    'TOKENPOCKET_TRANSFER_V2',
  ].includes(normalizedUpper(order.paymentQrType))) return null

  try {
    return JSON.parse(normalized(order.paymentQrContent)) as TokenPocketTransferPayload
  } catch {
    return null
  }
}

export const isSafeTokenPocketUsdtPayload = (
  order: RechargePaymentQrOrder | null | undefined,
) => {
  const payload = parseTokenPocketPayload(order)
  if (!order || !payload) return false

  const actionId = normalized(payload.actionId)
  const validActionIds = [normalized(order.orderId), normalized(order.orderNo)].filter(Boolean)
  const expectedAmount = Number(order.expectedAmount)

  return (
    normalized(payload.protocol) === 'TokenPocket'
    && normalized(payload.version) === '2.0'
    && normalized(payload.action) === 'transfer'
    && validActionIds.includes(actionId)
    && normalized(payload.blockchain).toLowerCase() === 'tron'
    && normalized(payload.to) === normalized(order.receiveAddress)
    && Number.isFinite(expectedAmount)
    && Number(payload.amount) === expectedAmount
    && normalized(payload.contract) === TRON_USDT_CONTRACT
    && normalizedUpper(payload.symbol) === 'USDT'
    && Number(payload.precision) === 6
  )
}

// Keep the amount-aware QR compact: fewer modules materially improve mobile
// camera recognition while retaining every field required to lock USDT-TRC20.
export const safeTokenPocketUsdtQrContent = (
  order: RechargePaymentQrOrder | null | undefined,
) => {
  const payload = parseTokenPocketPayload(order)
  if (!order || !payload || !isSafeTokenPocketUsdtPayload(order)) return ''

  return JSON.stringify({
    protocol: 'TokenPocket',
    version: '2.0',
    action: 'transfer',
    actionId: normalized(payload.actionId),
    blockchain: 'tron',
    to: normalized(order.receiveAddress),
    amount: Number(order.expectedAmount),
    contract: TRON_USDT_CONTRACT,
    symbol: 'USDT',
    precision: 6,
  })
}

export const safeUsdtAddressQrContent = (
  order: RechargePaymentQrOrder | null | undefined,
) => (isSafeTokenPocketUsdtPayload(order) ? normalized(order?.receiveAddress) : '')

export const safePaymentQrContent = (
  order: RechargePaymentQrOrder | null | undefined,
  usdtMode: RechargeUsdtQrMode = 'amount',
) => {
  if (!order) return ''

  if (isUsdtRechargeAsset(order.assetCode)) {
    return usdtMode === 'address'
      ? safeUsdtAddressQrContent(order)
      : safeTokenPocketUsdtQrContent(order)
  }

  if (
    normalizedUpper(order.assetCode) === 'TRX'
    && normalizedUpper(order.paymentQrType) === 'TRX_NATIVE'
  ) {
    return normalized(order.paymentQrContent)
  }

  return ''
}
